import React, { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Row, Col, Card } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as Icon from 'react-feather';
import { useNavigate, useParams } from 'react-router-dom';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormToggle from '../../../components/form/FormToggle';
import FormTextarea from '../../../components/form/FormTextarea';
import FormTags from '../../../components/form/FormTags';
import AdminHeader from '../../../components/header/AdminHeader';
import CallexAiAgentSettingsPage from './CallexAiAgentSettingsPage';
import CallexLoader from '../../../components/CallexLoader';

import {
  callexAiAgentAdd,
  callexAiAgentUpdate,
  callexAiAgentDetailsGetById,
  callexVoiceListGet,
  callexElevenVoiceListGet,
  voiceTtsPreview
} from '../../../store/slices/tenantSlices/configurationSlice';

// ---------------- OPTIONS ----------------

export const STATUS_OPTIONS = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' }
];

export const LANGUAGE_OPTIONS = [
  { label: 'English', value: 'en' },
  { label: 'Hindi', value: 'hi' },
  { label: 'Gujarati', value: 'gu' }
];

// Voice (TTS) providers — a fixed set, same as the reference portal.
export const VOICE_PROVIDER_OPTIONS = [
  { label: 'Local (Current Voice)', value: 'local' },
  { label: 'ElevenLabs', value: 'elevenlabs' },
  { label: 'Sarvam AI', value: 'sarvam' }
];

// LLM providers. "local" = the on-box gateway (default). "openai"/"gemini"
// route to those external APIs; the model is fixed per provider, so it is
// carried in the label rather than a separate dropdown.
export const LLM_PROVIDER_OPTIONS = [
  { label: 'Default / Local', value: 'local' },
  { label: 'OpenAI (gpt-4.1-mini)', value: 'openai' },
  { label: 'Google Gemini (gemini-2.5-flash)', value: 'gemini' }
];

// Sarvam speakers are a fixed set per model (bulbul:v2). Unlike ElevenLabs
// (fetched from an API), they never change, so the list is static.
export const SARVAM_VOICES = [
  { voice_id: 'anushka', name: 'Anushka', labels: { gender: 'female' } },
  { voice_id: 'manisha', name: 'Manisha', labels: { gender: 'female' } },
  { voice_id: 'vidya', name: 'Vidya', labels: { gender: 'female' } },
  { voice_id: 'arya', name: 'Arya', labels: { gender: 'female' } },
  { voice_id: 'abhilash', name: 'Abhilash', labels: { gender: 'male' } },
  { voice_id: 'karun', name: 'Karun', labels: { gender: 'male' } },
  { voice_id: 'hitesh', name: 'Hitesh', labels: { gender: 'male' } }
];

// Languages the voice-preview backend can synthesize (shown beside the
// Preview Text box for providers whose voices have no ready sample).
export const PREVIEW_LANG_OPTIONS = [
  { label: 'English', value: 'en' },
  { label: 'Hindi', value: 'hi' },
  { label: 'Gujarati', value: 'gu' }
];

// Resolves a language code (short "hi" or regional "hi-IN") to its full label.
export const getLanguageLabel = (lang) => {
  if (!lang) return '-';
  const base = String(lang).split('-')[0].toLowerCase();
  return (
    LANGUAGE_OPTIONS.find((opt) => opt.value.split('-')[0].toLowerCase() === base)?.label || lang
  );
};

export const BG_NOISE_OPTIONS = [
  { label: 'None', value: 'none' },
  { label: 'Light office', value: 'light' },
  { label: 'Moderate', value: 'moderate' },
  { label: 'Heavy', value: 'heavy' }
];

// Call-configuration defaults sent when creating an agent. These are managed
// afterwards via the Call Configuration modal on the agents listing.
const CALL_CONFIG_DEFAULTS = {
  max_call_duration: 600,
  silence_timeout: 20,
  transfer_enabled: false,
  transfer_message: '',
  transfer_number: '',
  transfer_country: '+91'
};

const slugify = (str = '') =>
  str
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

// Turns a raw voice tag/label (e.g. "gender:female" or "female") into a
// readable chip label.
const formatTag = (tag) => {
  const value = String(tag).split(':')[1] || tag;
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// ElevenLabs plan gating: only "premade" voices work on the free plan.
// "professional"/"cloned"/"generated"/"famous" library voices need a paid
// (Enterprise) subscription — flag them so the user knows which selection
// requires an upgrade.
const PAID_CATEGORIES = new Set(['professional', 'cloned', 'generated', 'famous']);

// Every provider returns a slightly different voice shape — normalise them all
// to { voice_id, name, sample_url, tags, category } so the card grid stays simple.
const normalizeVoice = (v) => ({
  voice_id: v.voice_id ?? v.external_id ?? v.id ?? v.name,
  name: v.display_name ?? v.name ?? v.voice_id,
  sample_url: v.preview_url ?? v.sample_url ?? '',
  tags: Array.isArray(v.tags) ? v.tags : Object.values(v.labels || {}).filter(Boolean),
  category: v.category ?? ''
});

const AddEditCallexAiAgent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { agentId } = useParams();
  const isEdit = Boolean(agentId);

  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  // Choose-Voice (audio preview) state.
  const audioRef = useRef(null);
  const objUrlRef = useRef(null);
  const reqIdRef = useRef(0);
  const [voiceList, setVoiceList] = useState([]);
  const [voiceListLoading, setVoiceListLoading] = useState(false);
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const [previewLoadingId, setPreviewLoadingId] = useState(null);
  const [previewText, setPreviewText] = useState('Hello, this is a voice preview.');
  const [previewLang, setPreviewLang] = useState('en');

  const initialValues = {
    name: '',
    slug: '',
    status: 'active',
    language: 'en-US',
    description: '',
    welcome_message: '',
    prompt: '',
    llm_provider: 'local',
    voice_provider: 'local',
    voice_id: '',
    background_noise: 'none',
    sentiment_fields: [{ field_name: '', description: '' }]
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    language: Yup.string().required('Language is required'),
    status: Yup.string().required('Status is required'),
    llm_provider: Yup.string().required('LLM provider is required'),
    voice_provider: Yup.string().required('Voice provider is required'),
    voice_id: Yup.string().required('Voice is required'),
    // slug is only part of the create payload and is not shown on the edit form.
    ...(isEdit ? {} : { slug: Yup.string().required('Slug is required') })
  });

  const onSubmit = async (vals) => {
    setLoading(true);
    try {
      const sentiment_fields = (vals.sentiment_fields || []).filter(
        (f) => f.field_name?.trim() || f.description?.trim()
      );
      const prompts = vals.prompt?.trim() || '';

      // TODO(BE): confirm the exact keys the callex-bot-agent API expects for
      // voice_provider / llm_provider / voice_id.
      const commonPayload = {
        name: vals.name,
        language: vals.language,
        voice_provider: vals.voice_provider || 'local',
        llm_provider: vals.llm_provider || 'local',
        voice_id: vals.voice_id,
        background_noise: vals.background_noise,
        prompts,
        welcome_message: vals.welcome_message,
        description: vals.description,
        status: vals.status,
        sentiment_fields
      };

      if (isEdit) {
        // Partial update — call-config fields are managed by the Call
        // Configuration modal and left untouched here.
        await dispatch(callexAiAgentUpdate({ id: agentId, ...commonPayload })).unwrap();
        toast.success('Agent updated successfully.');
      } else {
        await dispatch(
          callexAiAgentAdd({ slug: vals.slug, ...commonPayload, ...CALL_CONFIG_DEFAULTS })
        ).unwrap();
        toast.success('Agent successfully created.');
      }
      navigate('/ai-callex/ai/callex-ai-agents');
    } catch (error) {
      console.log('error', error);
    } finally {
      setLoading(false);
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    setFieldValue,
    setValues,
    validateForm
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  // ElevenLabs voices ship their own preview_url, so custom-text synthesis is
  // only needed (and only offered) for the other providers.
  const supportsCustomPreview = values.voice_provider !== 'elevenlabs';

  // Only ElevenLabs voices carry a plan category; the tag tells the user which
  // selections need a paid subscription.
  const planTagFor = (voice) => {
    if (values.voice_provider !== 'elevenlabs' || !voice.category) return null;
    return PAID_CATEGORIES.has(voice.category)
      ? { label: 'Enterprise', className: 'text-warning border-warning' }
      : { label: 'Free', className: 'text-success border-success' };
  };

  // Prefill on edit.
  useEffect(() => {
    if (isEdit && agentId) {
      setIsFetching(true);
      dispatch(callexAiAgentDetailsGetById(agentId))
        .unwrap()
        .then((res) => {
          const data = res?.data;
          if (data) {
            setValues({
              ...initialValues,
              name: data.name ?? '',
              slug: data.slug ?? '',
              status: data.status ?? 'active',
              language: data.language ?? 'en-US',
              description: data.description ?? '',
              welcome_message: data.welcome_message ?? '',
              prompt: Array.isArray(data.prompts) ? (data.prompts[0] ?? '') : (data.prompts ?? ''),
              llm_provider: data.llm_provider ?? 'local',
              voice_provider: data.voice_provider ?? 'local',
              voice_id: data.voice_id ?? '',
              background_noise: data.background_noise ?? 'none',
              sentiment_fields:
                Array.isArray(data.sentiment_fields) && data.sentiment_fields.length > 0
                  ? data.sentiment_fields
                  : [{ field_name: '', description: '' }]
            });
          }
        })
        .catch((error) => console.log('error', error))
        .finally(() => setIsFetching(false));
    }
  }, [isEdit, agentId]);

  // Resolve the voice list for the selected provider: Sarvam is a fixed list,
  // Local + ElevenLabs are fetched.
  const fetchVoices = async (provider) => {
    setVoiceListLoading(true);
    try {
      const action = provider === 'elevenlabs' ? callexElevenVoiceListGet() : callexVoiceListGet();
      const response = await dispatch(action).unwrap();
      const data = response?.data || [];
      setVoiceList(data.map(normalizeVoice));
    } catch (error) {
      setVoiceList([]);
      console.log('error', error);
    } finally {
      setVoiceListLoading(false);
    }
  };

  useEffect(() => {
    const provider = values.voice_provider;
    if (!provider) {
      setVoiceList([]);
      return;
    }
    if (provider === 'sarvam') {
      setVoiceList(SARVAM_VOICES.map(normalizeVoice));
      setVoiceListLoading(false);
      return;
    }
    fetchVoices(provider);
  }, [values.voice_provider]);

  // Tear down audio + object URL on unmount so a playing sample doesn't outlive
  // the component or leak its blob URL.
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (objUrlRef.current) {
        URL.revokeObjectURL(objUrlRef.current);
        objUrlRef.current = null;
      }
    };
  }, []);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (objUrlRef.current) {
      URL.revokeObjectURL(objUrlRef.current);
      objUrlRef.current = null;
    }
    setPlayingVoiceId(null);
  };

  const startAudio = (src, voiceId) => {
    if (!audioRef.current) audioRef.current = new Audio();
    const audio = audioRef.current;
    audio.src = src;
    audio.onended = () => setPlayingVoiceId(null);
    audio.onerror = () => {
      setPlayingVoiceId(null);
      toast.error('Audio not available.');
    };
    audio
      .play()
      .then(() => setPlayingVoiceId(voiceId))
      .catch(() => setPlayingVoiceId(null));
  };

  // Preview a voice: voices with a ready sample play it directly; voices without
  // one are synthesized from the typed Preview Text in the chosen language.
  const handlePreviewToggle = async (voice) => {
    if (playingVoiceId === voice.voice_id) {
      stopAudio();
      return;
    }
    stopAudio();

    // Supersede any in-flight synth request so a late one can't hijack playback.
    const reqId = ++reqIdRef.current;

    if (voice.sample_url) {
      startAudio(voice.sample_url, voice.voice_id);
      return;
    }

    if (!supportsCustomPreview || !previewText.trim()) return;

    try {
      setPreviewLoadingId(voice.voice_id);
      const blob = await dispatch(
        voiceTtsPreview({
          provider: values.voice_provider,
          voice_id: voice.voice_id,
          text: previewText.trim(),
          language: previewLang
        })
      ).unwrap();
      if (reqId !== reqIdRef.current) return;
      const url = URL.createObjectURL(blob);
      objUrlRef.current = url;
      startAudio(url, voice.voice_id);
    } catch (error) {
      console.log('error', error);
      toast.error('Voice preview is not available yet.');
    } finally {
      if (reqId === reqIdRef.current) setPreviewLoadingId(null);
    }
  };

  // Clear the chosen voice when the provider changes so we never persist a
  // provider/voice_id mismatch. (Programmatic prefill on edit doesn't run this.)
  const handleProviderChange = (e) => {
    stopAudio();
    handleChange(e);
    setFieldValue('voice_id', '');
  };

  const handleNameChange = (e) => {
    const val = e.target.value;
    setFieldValue('name', val);
    // Auto-derive the slug from the name until the user edits the slug themselves.
    if (!isEdit && !slugTouched) {
      setFieldValue('slug', slugify(val));
    }
  };

  const handleSlugChange = (e) => {
    setSlugTouched(true);
    setFieldValue('slug', e.target.value);
  };

  const addSentimentField = () => {
    setFieldValue('sentiment_fields', [
      ...values.sentiment_fields,
      { field_name: '', description: '' }
    ]);
  };

  const removeSentimentField = (index) => {
    const updated = values.sentiment_fields.filter((_, i) => i !== index);
    setFieldValue('sentiment_fields', updated);
  };

  const selectedVoice = voiceList.find((v) => v.voice_id === values.voice_id);

  return (
    <React.Fragment>
      <AdminHeader
        title={isEdit ? 'Edit AI Agent' : 'Create AI Agent'}
        backBtnName="Back"
        addBtnName={isEdit ? 'Update Agent' : 'Create Agent'}
        isBack
        isAdd
        onBackClick={() => navigate('/ai-callex/ai/callex-ai-agents')}
        form_id="callex-ai-agent-form"
        isLoading={loading}
        isAddDisabled={isFetching}
      />
      {isFetching ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-3"
          style={{ height: 'calc(100vh - 200px)' }}>
          <CallexLoader />
        </Card>
      ) : (
        <CallexAiAgentSettingsPage handleSubmit={handleSubmit} validateForm={validateForm}>
          {/* BASIC INFO SECTION */}
          <div id="core" className="agent-section">
            <div className="context-card">
              <Row>
                <Col lg="4">
                  <FormInput
                    name="name"
                    label="Agent Name"
                    placeholder="Enter Agent Name"
                    value={values.name}
                    onChange={handleNameChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="4">
                  <FormToggle
                    name="status"
                    label="Status"
                    value={String(values.status === 'active')}
                    onChange={() =>
                      setFieldValue('status', values.status === 'active' ? 'inactive' : 'active')
                    }
                  />
                </Col>
                <Col lg="4">
                  <FormSelect
                    name="language"
                    label="Agent Language"
                    options={LANGUAGE_OPTIONS}
                    value={values.language}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="6">
                  <FormInput
                    name="slug"
                    label="Slug"
                    placeholder="auto-generated-from-name"
                    value={values.slug}
                    onChange={handleSlugChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    disabled={isEdit}
                  />
                </Col>
                <Col lg="6">
                  <FormInput
                    name="description"
                    label="Description"
                    placeholder="Enter Description"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="12">
                  <FormTextarea
                    name="welcome_message"
                    label="Welcome Message"
                    placeholder="First sentence on call"
                    value={values.welcome_message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    rows={3}
                  />
                </Col>
                <Col lg="12">
                  <FormTextarea
                    name="prompt"
                    label='Train your Script ("System Prompt")'
                    placeholder="LLM instructions"
                    value={values.prompt}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    rows={4}
                  />
                </Col>
              </Row>
            </div>
          </div>

          {/* MODEL / LLM SECTION */}
          <div id="model" className="agent-section">
            <div className="context-card">
              <Row>
                <Col lg="6">
                  <FormSelect
                    name="llm_provider"
                    label="LLM Provider"
                    placeholder="Select LLM Provider"
                    options={LLM_PROVIDER_OPTIONS}
                    value={values.llm_provider}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
              </Row>
            </div>
          </div>

          {/* VOICE & SPEECH SECTION */}
          <div id="voice_speech" className="agent-section">
            <div className="context-card">
              <Row>
                <Col lg="6">
                  <FormSelect
                    name="voice_provider"
                    label="Voice Provider"
                    placeholder="Select Voice Provider"
                    options={VOICE_PROVIDER_OPTIONS}
                    value={values.voice_provider}
                    onChange={handleProviderChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
                <Col lg="6">
                  <FormInput
                    name="voice_id"
                    label="Selected Voice"
                    placeholder="Select a voice from the list below"
                    value={selectedVoice?.name || values.voice_id}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    disabled
                  />
                </Col>
                <Col lg="6">
                  <FormSelect
                    name="background_noise"
                    label="Background Noise"
                    placeholder="Select Background Noise"
                    options={BG_NOISE_OPTIONS}
                    value={values.background_noise}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                  />
                </Col>
              </Row>
            </div>

            {/* CHOOSE VOICE — preview (play / synthesize) + select */}
            {values.voice_provider ? (
              <div className="context-card">
                <label className="form-label mb-3 fw-bold">Choose Voice</label>

                {/* Preview text + language — only for providers whose voices
                    have no ready sample and must be synthesized. */}
                {supportsCustomPreview && (
                  <Row className="align-items-end mb-3">
                    <Col lg="8">
                      <FormInput
                        name="__preview_text"
                        label="Preview Text"
                        placeholder="Type text to preview the voice"
                        value={previewText}
                        onChange={(e) => setPreviewText(e.target.value)}
                      />
                    </Col>
                    <Col lg="4">
                      <FormSelect
                        name="__preview_lang"
                        label="Language"
                        options={PREVIEW_LANG_OPTIONS}
                        value={previewLang}
                        onChange={(e) => setPreviewLang(e.target.value)}
                      />
                    </Col>
                  </Row>
                )}

                {!voiceListLoading && voiceList?.length > 0 ? (
                  <Row>
                    {voiceList.map((voice, index) => {
                      const planTag = planTagFor(voice);
                      return (
                        <Col lg="6" className="mb-3" key={index}>
                          <div
                            className={`voice-card p-3 rounded border ${
                              values.voice_id === voice.voice_id ? 'selected-card' : ''
                            }`}
                            onClick={() => setFieldValue('voice_id', voice.voice_id)}
                            style={{
                              cursor: 'pointer',
                              background: '#ffffff',
                              transition: '0.2s ease'
                            }}>
                            {/* Top Row */}
                            <div className="d-flex justify-content-between align-items-center">
                              <div
                                className="d-flex align-items-center gap-2"
                                style={{ minWidth: 0 }}>
                                <div className="fw-semibold text-truncate">{voice.name}</div>
                                {planTag && (
                                  <span
                                    className={`badge bg-transparent border ${planTag.className}`}
                                    style={{
                                      fontSize: '10px',
                                      padding: '3px 8px',
                                      borderRadius: '20px',
                                      whiteSpace: 'nowrap',
                                      flexShrink: 0
                                    }}>
                                    {planTag.label}
                                  </span>
                                )}
                              </div>
                              <div
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePreviewToggle(voice);
                                }}
                                style={{ cursor: 'pointer' }}
                                title="Preview voice">
                                {previewLoadingId === voice.voice_id ? (
                                  <div className="icon-action info">
                                    <Icon.Loader size={16} className="text-info" />
                                  </div>
                                ) : playingVoiceId === voice.voice_id ? (
                                  <div className="icon-action danger">
                                    <Icon.Square size={16} className="text-danger" />
                                  </div>
                                ) : (
                                  <div className="icon-action info">
                                    <Icon.Play size={16} className="text-info" />
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Tags */}
                            {voice.tags?.length > 0 && (
                              <div className="mt-2">
                                <FormTags tags={voice.tags} formatTag={formatTag} />
                              </div>
                            )}
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                ) : (
                  <div className="p-0 d-flex justify-content-center align-items-center no-data m-0">
                    {voiceListLoading ? 'Loading Voices...' : 'No voice available'}
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {/* SENTIMENT FIELDS SECTION */}
          <div id="sentiment_fields" className="agent-section">
            <div className="context-card">
              <label className="form-label mb-3 fw-bold">Sentiment Fields</label>

              <Row className="g-2">
                {!Array.isArray(values.sentiment_fields) || values.sentiment_fields.length === 0 ? (
                  <Col lg="12" className="text-center p-4 border rounded bg-light">
                    <div className="d-flex flex-column align-items-center">
                      <span className="text-muted small mb-2">No sentiment fields defined.</span>
                      <div className="icon-action info" onClick={addSentimentField}>
                        <Icon.Plus size={16} />
                      </div>
                    </div>
                  </Col>
                ) : (
                  values.sentiment_fields.map((item, index) => (
                    <Col lg="12" key={index} className="mb-2 pb-2 border-bottom">
                      <Row className="align-items-center g-2">
                        <Col lg="4">
                          <FormInput
                            name={`sentiment_fields[${index}].field_name`}
                            label="Field Name"
                            placeholder="Enter field name"
                            value={item.field_name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            touched={touched}
                            errors={errors}
                          />
                        </Col>
                        <Col lg="7">
                          <FormInput
                            name={`sentiment_fields[${index}].description`}
                            label="Description"
                            placeholder="Enter description"
                            value={item.description}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            touched={touched}
                            errors={errors}
                          />
                        </Col>
                        <Col lg="1" className="pb-1">
                          <div className="d-flex gap-1 justify-content-center pt-3">
                            {values.sentiment_fields.length > 1 && (
                              <div
                                className="icon-action danger"
                                onClick={() => removeSentimentField(index)}>
                                <Icon.Trash size={14} />
                              </div>
                            )}
                            {index === values.sentiment_fields.length - 1 && (
                              <div className="icon-action info" onClick={addSentimentField}>
                                <Icon.Plus size={14} />
                              </div>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  ))
                )}
              </Row>
            </div>
          </div>
        </CallexAiAgentSettingsPage>
      )}
    </React.Fragment>
  );
};

export default AddEditCallexAiAgent;
